import { trackerEvents } from "../constants.js";
import { trackerStore } from "./Store.js";

export class ChapterRecords {
	static HEARTBEAT_KEY = "Heartbeat";
	static START_KEY = "HeartbeatStart";

	constructor(chapterId, epub_id) {
		this.chapterId = epub_id + chapterId;
		this.store = trackerStore;
	}

	/** Returns the number of the last session recorded for a chapter */
	getIncrement() {
		const lastIncrement = this.store.getKeySync(this.chapterId);
		let value = 0;
		if (!lastIncrement) {
			this.store.setKeySync(this.chapterId, "0");
		} else {
			value = parseInt(lastIncrement, 10);
		}
		return value;
	}

	/** Increments and stores the total number of records for a chapter, including the current one */
	incrementSession() {
		let chapterIncrement = this.getIncrement();
		chapterIncrement = 1 + chapterIncrement;
		this.store.setKeySync(this.chapterId, chapterIncrement.toString());
	}

	/** Starts and stores a new record for a session, and ends the last one if any has been stored */
	startSession(session) {
		this.startHeartbeat();
		let sessionIncrement = this.getIncrement();
		let currentRecord = this.store.getKeySync(
			this.chapterId + sessionIncrement.toString(),
		);
		if (currentRecord !== null) {
			this.endSession(trackerEvents.Terminated);
			sessionIncrement = this.getIncrement();
		}
		this.store.setKeySync(
			this.chapterId + sessionIncrement.toString(),
			JSON.stringify(session),
		);
	}

	/** Returns all available records for a session */
	getSessions() {
		let logNumber = this.getIncrement();
		let sessions = [];
		for (let i = 0; i <= logNumber; i++) {
			let session = this.store.getKeySync(this.chapterId + i.toString());
			if (session !== null) {
				const deserializedSession = JSON.parse(session);
				sessions.push(deserializedSession);
			}
		}
		return sessions;
	}

	/**
	 * Returns every stored record with its index. Records with an index
	 * below getIncrement() have ended; the one at getIncrement(), if any,
	 * is the session being recorded.
	 */
	getSessionEntries() {
		const logNumber = this.getIncrement();
		const entries = [];
		for (let i = 0; i <= logNumber; i++) {
			const session = this.store.getKeySync(this.chapterId + i.toString());
			if (session !== null) {
				entries.push({ index: i, session: JSON.parse(session) });
			}
		}
		return entries;
	}

	/** Removes ended records, by index, once they have been delivered. */
	removeSessions(indices) {
		const current = this.getIncrement();
		for (const index of indices) {
			if (index < current) {
				this.store.removeKeySync(this.chapterId + index.toString());
			}
		}
	}

	/** Updates the session being currently recorded */
	updateSession(dataField, data, arrayField) {
		let sessionIncrement = this.getIncrement();
		let session = this.store.getKeySync(
			this.chapterId + sessionIncrement.toString(),
		);
		if (session !== null) {
			let deserializedSession = JSON.parse(session);
			if (!deserializedSession[dataField] && arrayField === true) {
				deserializedSession[dataField] = [];
			}

			if (arrayField === true) {
				deserializedSession[dataField].push(data);
			} else {
				deserializedSession[dataField] = data;
			}

			this.store.setKeySync(
				this.chapterId + sessionIncrement.toString(),
				JSON.stringify(deserializedSession),
			);
		}
	}

	/** Stops and marks the last recorded session with a timestamp */
	endSession(trackerEvent) {
		let sessionIncrement = this.getIncrement();
		let session = this.store.getKeySync(
			this.chapterId + sessionIncrement.toString(),
		);
		if (session !== null) {
			let deserializedSession = JSON.parse(session);
			deserializedSession.eT = new Date().toISOString();
			if (trackerEvent) {
				deserializedSession.tE = trackerEvent;
			}
			this.store.setKeySync(
				this.chapterId + sessionIncrement.toString(),
				JSON.stringify(deserializedSession),
			);
			this.incrementSession();
		}
	}

	/** Returns the increment and time for the current Heartbeat in a session */
	getHeartbeat() {
		const heartbeat = this.store.getKeySync(
			this.chapterId + ChapterRecords.HEARTBEAT_KEY,
		);
		return JSON.parse(heartbeat);
	}

	/** Sets and stores the increment and time for the current heartbeat in the session */
	setHeartbeat(heartbeat) {
		heartbeat.increment = 1 + heartbeat.increment;
		heartbeat.time = new Date().toISOString();
		this.store.setKeySync(
			this.chapterId + ChapterRecords.HEARTBEAT_KEY,
			JSON.stringify(heartbeat),
		);
	}

	/** Starts the increment and time for the current heartbeat, as well as the time the session started */
	startHeartbeat() {
		const heartbeat = {
			increment: 1,
		};
		this.setHeartbeat(heartbeat);
		this.store.setKeySync(
			this.chapterId + ChapterRecords.START_KEY,
			JSON.stringify(new Date().toISOString()),
		);
	}
}
